import useSWR from 'swr';
import {useAuth} from '../../../hooks/useAuth';
import {AXIOS} from '../../../services/api';

/**
 * Custom hook to fetch account-related data (overview, apps, reviews, analytics).
 *
 * @returns {object} An object containing the fetched data and any errors.
 * - overviewData: The overview data or null if still loading or not available.
 * - appListData: The application list data or null.
 * - reviewData: The review data or null.
 * - analyticsData: The analytics data or null.
 * - isLoading: A boolean indicating whether the data is still loading.
 * - error: An error object if any of the fetches failed or null.
 */
function useDashboardData() {
	const { user } = useAuth();
	const accountId = user?.accountId;

	const endpoints = {
		overview: accountId ? `accounts/${accountId}/overview` : null,
		apps: accountId ? `accounts/${accountId}/applications` : null,
		reviews: accountId ? `accounts/${accountId}/reviews` : null,
		analytics: accountId ? `accounts/${accountId}/analytics` : null,
	};

	const fetcher = (url) => AXIOS.get(url).then((res) => res.data);

	const { data: overviewResponse, error: overviewError } = useSWR(endpoints.overview, fetcher);
	const { data: appsResponse, error: appsError } = useSWR(endpoints.apps, fetcher);
	const { data: reviewsResponse, error: reviewsError } = useSWR(endpoints.reviews, fetcher);
	const { data: analyticsResponse, error: analyticsError } = useSWR(endpoints.analytics, fetcher);

	const isLoading = !overviewResponse && !appsResponse && !reviewsResponse && !analyticsResponse;

	const overviewData = overviewResponse?.data
		? [
			{ label: 'Total Apps', value: String(overviewResponse.data.totalApps), icon: 'faMobile' },
			{ label: 'Play Store Apps', value: String(overviewResponse.data.playStoreApps), icon: 'faGooglePlay' },
			{ label: 'Apple Store Apps', value: String(overviewResponse.data.appleStoreApps), icon: 'faAppStore' },
			{ label: 'Total Reviews', value: overviewResponse.data.totalReviews, icon: 'faStar' },
		]
		: null;

	const appListData = appsResponse?.data || null;
	const reviewData = reviewsResponse?.data?.data || null;
	const analyticsData = analyticsResponse?.data || null;

	const error = overviewError || appsError || reviewsError || analyticsError;

	return {
		overviewData,
		appListData,
		reviewData,
		analyticsData,
		isLoading,
		error,
	};
}

export default useDashboardData;
